import { z } from 'zod'
import { COMMS_KEY } from './comms'
import { PushTemplateContentSchema, type PushEnvelope } from './comms-push'
import { SmsTemplateContentSchema, type SmsEnvelope } from './comms-sms'
import { WORKER_TYPES } from './workforce'
import { HtmlTemplateRefSchema, isImportedHtml, type ImportedHtmlContent } from './comms-html'

// Communications, Phase 2 (ADR 0035; docs/architecture/BRE-Communications-Plan.md
// §5.5, §6.3, §7, §9, §11): templates, segments, campaigns, the `message`
// outbox and its provider events.
//
// Shared by Platform Core (which renders and decides), the web Worker (which
// sends through a provider and reports back) and the staff UI (which shows
// the same rendering as a live preview). Nothing here sends anything.

// --------------------------------------------------------------- messages

// One row per recipient per send (`message`), never deleted.
//   queued           waiting for the dispatcher (and, after a soft failure,
//                    for its next attempt)
//   sending          claimed by one dispatch (a claim id and a lease)
//   accepted         the provider took it. NOT delivered: Hub never shows an
//                    accepted message as delivered (plan §2, principle 5)
//   simulated        nothing was sent (simulated mode); the row is the record
//   delivered        the provider says the receiving server accepted it
//   deferred         a soft bounce; the provider keeps trying
//   bounced          a bounce the provider gave up on
//   complained       the recipient marked it as spam
//   failed           not sent after the last attempt, or refused for good
//   suppressed       not sent: the address is suppressed (or the provider
//                    says it is inactive)
//   skipped          not sent: another gate at dispatch (no consent any more,
//                    frequency cap, person no longer active, ...)
//   outcome_unknown  claimed and never reported within its lease: it may have
//                    gone. Never resent (ADR 0008)
//   undelivered      a text the carrier or Twilio couldn't deliver after
//                    accepting it (Phase 3)
export const MESSAGE_STATUSES = ['queued', 'sending', 'accepted', 'simulated', 'delivered', 'deferred', 'bounced', 'complained', 'failed', 'suppressed', 'skipped', 'outcome_unknown', 'undelivered'] as const
export type MessageStatus = (typeof MESSAGE_STATUSES)[number]
export const MESSAGE_STATUS_LABELS: Record<MessageStatus, string> = {
  queued: 'Queued',
  sending: 'Sending',
  accepted: 'Accepted by provider',
  simulated: 'Simulated',
  delivered: 'Delivered',
  deferred: 'Deferred',
  bounced: 'Bounced',
  complained: 'Spam complaint',
  failed: 'Failed',
  suppressed: 'Suppressed',
  skipped: 'Skipped',
  outcome_unknown: 'Outcome unknown',
  undelivered: 'Undelivered',
}
// Never sent, and never will be.
export const MESSAGE_NOT_SENT: readonly MessageStatus[] = ['failed', 'suppressed', 'skipped']
// Handed over (or recorded as handed over, in simulated mode).
export const MESSAGE_HANDED_OVER: readonly MessageStatus[] = ['accepted', 'simulated', 'delivered', 'deferred', 'bounced', 'complained', 'undelivered']

export const MESSAGE_EVENT_KINDS = [
  'queued',
  'claimed',
  'accepted',
  'simulated',
  'delivered',
  'deferred',
  'bounced',
  'complained',
  'unsubscribed',
  'failed',
  'retry_scheduled',
  'released',
  'suppressed',
  'skipped',
  'outcome_unknown',
  // Phase 3: a carrier took a text (Twilio's "sent"), couldn't deliver it,
  // or Hub held it (quiet hours).
  'sent',
  'undelivered',
  'held',
] as const
export type MessageEventKind = (typeof MESSAGE_EVENT_KINDS)[number]

export const COMMS_PROVIDERS = ['cloudflare', 'postmark', 'simulated', 'twilio', 'mybre'] as const
export type CommsProvider = (typeof COMMS_PROVIDERS)[number]
export const COMMS_PROVIDER_LABELS: Record<CommsProvider, string> = { cloudflare: 'Cloudflare Email Service', postmark: 'Postmark', simulated: 'Simulated (nothing sent)', twilio: 'Twilio', mybre: 'myBRE push' }

// Where a message came from. Campaigns and tests and confirmations are this
// outbox's own; reminder digests and governance emails keep their own tables
// and only REPORT here, so a person's history shows everything.
export const MESSAGE_SOURCES = ['campaign', 'test', 'confirmation', 'reminder_digest', 'governance_email', 'sms_optin', 'journey', 'event_alert'] as const
export type MessageSource = (typeof MESSAGE_SOURCES)[number]
export const MESSAGE_SOURCE_LABELS: Record<MessageSource, string> = {
  campaign: 'Campaign',
  test: 'Test send',
  confirmation: 'Subscription confirmation',
  reminder_digest: 'Reminder email',
  governance_email: 'Governance email',
  sms_optin: 'Text sign-up confirmation',
  journey: 'Journey',
  event_alert: 'Event alert',
}

// ---------------------------------------------------------- the route rule

// Two kinds of sending, never mixed (plan §5.1, §12):
//   marketing     any purpose that needs consent. ONLY the campaign
//                 provider (Postmark's broadcast stream) or simulated.
//                 Cloudflare Email Service's terms say it is for
//                 transactional email only, and a block on that account
//                 would also stop BRE Auth's and BTownTix's mail.
//   operational   operational, service and governance mail, and the
//                 double opt-in confirmation: Cloudflare Email Service (or
//                 simulated).
export type SendClass = 'marketing' | 'operational'
export const sendClassOf = (purpose: { needsConsent: boolean }): SendClass => (purpose.needsConsent ? 'marketing' : 'operational')

export class ProviderRouteError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'ProviderRouteError'
  }
}

// Every send goes through this. It throws rather than return false, so a
// caller that forgets to look still can't send marketing the wrong way.
export function assertProviderRoute(provider: CommsProvider, sendClass: SendClass, channel: 'email' | 'sms' | 'push' = 'email'): void {
  if (provider === 'simulated') return
  // Pushes go only through myBRE (ADR 0037), and myBRE carries nothing but pushes.
  if (channel === 'push' || provider === 'mybre') {
    if (channel !== 'push' || provider !== 'mybre') throw new ProviderRouteError(channel === 'push' ? `A push never goes through ${COMMS_PROVIDER_LABELS[provider]}` : 'myBRE carries pushes only')
    return
  }
  // Texts go only through Twilio (each kind of sending on its own Messaging
  // Service), and Twilio carries nothing but texts.
  if (channel === 'sms' || provider === 'twilio') {
    if (channel !== 'sms' || provider !== 'twilio') throw new ProviderRouteError(channel === 'sms' ? `A text never goes through ${COMMS_PROVIDER_LABELS[provider]}` : 'Twilio carries texts only')
    return
  }
  if (sendClass === 'marketing' && provider !== 'postmark') throw new ProviderRouteError(`Marketing email never goes through ${COMMS_PROVIDER_LABELS[provider]}`)
  if (sendClass === 'operational' && provider !== 'cloudflare') throw new ProviderRouteError(`Operational email goes through Cloudflare Email Service, not ${COMMS_PROVIDER_LABELS[provider]}`)
}

// ------------------------------------------------------------- templates

// The body is a short list of blocks, never HTML: Hub renders it to email
// HTML and plain text itself, so a template can't carry a script, a style
// that hides the footer, a tracking pixel or an off-brand layout.
//
// Inline text (in `text`, `list` items and the event card's lines) takes
// **bold**, *italic* and [a link](https://...). Paragraphs are separated by a
// blank line. Merge fields are {{field}} or {{field|fallback}}, from the
// fixed list below only; every value is HTML-escaped where it lands.

export const MERGE_FIELDS = {
  first_name: { label: 'First name', fallback: 'there', url: false },
  full_name: { label: 'Full name', fallback: 'friend', url: false },
  event_name: { label: 'Event name (set on the campaign)', fallback: 'our next event', url: false },
  event_date: { label: 'Event date (set on the campaign)', fallback: 'soon', url: false },
  mybre_url: { label: 'myBRE link', fallback: '', url: true },
  preference_url: { label: 'Preference center link', fallback: '', url: true },
} as const
export type MergeField = keyof typeof MERGE_FIELDS
export const MERGE_FIELD_KEYS = Object.keys(MERGE_FIELDS) as MergeField[]
const MERGE = /\{\{\s*([a-z_]+)\s*(?:\|([^{}|]{0,40}))?\}\}/g

const SafeText = z.string().trim().min(1)
const HttpsUrl = z
  .string()
  .trim()
  .max(500)
  .refine((v) => /^https:\/\/[^\s<>"']+$/.test(v) || /^\{\{\s*(mybre_url|preference_url)\s*\}\}$/.test(v), 'Use an https:// link, {{mybre_url}} or {{preference_url}}')

export const TemplateBlockSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('heading'), text: SafeText.max(200) }),
  z.object({ type: z.literal('text'), text: SafeText.max(5000) }),
  z.object({ type: z.literal('button'), label: SafeText.max(60), url: HttpsUrl }),
  z.object({ type: z.literal('list'), items: z.array(SafeText.max(500)).min(1).max(20) }),
  z.object({ type: z.literal('event'), name: SafeText.max(200), when: SafeText.max(200), where: z.string().trim().max(200).nullable(), url: HttpsUrl.nullable() }),
  z.object({ type: z.literal('divider') }),
])
export type TemplateBlock = z.infer<typeof TemplateBlockSchema>
export const TEMPLATE_BLOCK_LABELS: Record<TemplateBlock['type'], string> = { heading: 'Heading', text: 'Text', button: 'Button', list: 'List', event: 'Event card', divider: 'Divider' }

export const TemplateContentSchema = z.object({
  subject: z.string().trim().min(1).max(150),
  preheader: z.string().trim().max(200).nullable(),
  blocks: z.array(TemplateBlockSchema).min(1).max(60),
})
export type TemplateContent = z.infer<typeof TemplateContentSchema>

// Every string a template's merge fields can appear in.
function templateStrings(content: TemplateContent): string[] {
  const out = [content.subject, content.preheader ?? '']
  for (const b of content.blocks) {
    if (b.type === 'heading' || b.type === 'text') out.push(b.text)
    else if (b.type === 'button') out.push(b.label, b.url)
    else if (b.type === 'list') out.push(...b.items)
    else if (b.type === 'event') out.push(b.name, b.when, b.where ?? '', b.url ?? '')
  }
  return out
}

// What's wrong with a template, in words staff can act on. Empty is good.
export function templateIssues(content: TemplateContent): string[] {
  const issues: string[] = []
  const used = new Set<string>()
  for (const s of templateStrings(content)) {
    for (const m of s.matchAll(MERGE)) {
      used.add(m[1]!)
      if (!(m[1]! in MERGE_FIELDS)) issues.push(`{{${m[1]}}} isn't a merge field Hub knows. Use one of: ${MERGE_FIELD_KEYS.map((k) => `{{${k}}}`).join(', ')}`)
    }
    // A brace pair that isn't a well-formed merge field.
    if (/\{\{|\}\}/.test(s.replace(MERGE, ''))) issues.push('A {{ or }} isn’t part of a well-formed merge field')
  }
  for (const m of [content.subject, content.preheader ?? ''].flatMap((s) => [...s.matchAll(MERGE)])) {
    if (MERGE_FIELDS[m[1] as MergeField]?.url) issues.push(`{{${m[1]}}} is a link and can't go in the subject or preheader`)
  }
  for (const b of content.blocks) {
    if (b.type !== 'text' && b.type !== 'list') continue
    for (const s of b.type === 'text' ? [b.text] : b.items) {
      for (const link of s.matchAll(/\[([^\]]*)\]\(([^)]*)\)/g)) {
        const url = link[2]!.trim()
        if (!/^https:\/\/[^\s<>"']+$/.test(url) && !/^\{\{\s*(mybre_url|preference_url)\s*\}\}$/.test(url)) issues.push(`The link “${link[1]}” must start with https://`)
      }
    }
  }
  return [...new Set(issues)]
}

export function mergeFieldsUsed(content: TemplateContent): MergeField[] {
  const used = new Set<MergeField>()
  for (const s of templateStrings(content)) for (const m of s.matchAll(MERGE)) if (m[1]! in MERGE_FIELDS) used.add(m[1] as MergeField)
  return [...used]
}

// ------------------------------------------------------------- rendering

export const escapeHtml = (value: string) => value.replace(/[&<>"']/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[ch]!)

export type RenderBrand = { name: string; legalName: string; postalAddress: string }
export type MergeValues = { first_name: string | null; full_name: string | null; event_name: string | null; event_date: string | null; mybre_url: string | null; preference_url: string | null }

export type RenderInput = {
  // Blocks, or an imported design (ADR 0042).
  content: TemplateContent | ImportedHtmlContent
  brand: RenderBrand
  sendClass: SendClass
  merge: MergeValues
  // Marketing needs both (the locked footer); operational mail may have none.
  links: { preferenceUrl: string | null; unsubscribeUrl: string | null }
  // Why this person gets this ("You subscribed to BRE news by email.").
  reason: string
  test?: boolean
  // The name at the top (the sender's display name); the brand's by default.
  masthead?: string | null
}

export type RenderedEmail = { subject: string; preheader: string | null; html: string; text: string }

export class RenderError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'RenderError'
  }
}

const safeHref = (url: string | null) => (url && /^https:\/\/[^\s<>"']+$/.test(url) ? url : null)

// A merge value for its field: the value, else the template's fallback,
// else the field's own fallback. Plain text; the caller escapes.
function mergeValue(field: MergeField, fallback: string | undefined, values: MergeValues): string {
  const v = values[field]
  if (v && v.trim()) return v.trim()
  if (fallback !== undefined) return fallback.trim()
  return MERGE_FIELDS[field].fallback
}

function substitute(raw: string, values: MergeValues, mode: 'html' | 'text'): string {
  return raw.replace(MERGE, (_all, field: string, fallback?: string) => {
    if (!(field in MERGE_FIELDS)) return ''
    const value = mergeValue(field as MergeField, fallback, values)
    return mode === 'html' ? escapeHtml(value) : value
  })
}

// Inline markdown on ESCAPED text, then the merge values (escaped) in one
// pass: a merge value is never read as markdown or as a link.
function inlineHtml(raw: string, values: MergeValues, linkColor: string): string {
  const escaped = escapeHtml(raw)
  const withLinks = escaped.replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (_all, label: string, url: string) => {
    const resolved = url.replace(MERGE, (_m, field: string) => (MERGE_FIELDS[field as MergeField]?.url ? (values[field as MergeField] ?? '') : ''))
    const href = safeHref(resolved.replace(/&amp;/g, '&'))
    return href ? `<a href="${escapeHtml(href)}" style="color:${linkColor};text-decoration:underline">${label}</a>` : label
  })
  const styled = withLinks.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>').replace(/(^|[^*])\*([^*\s][^*]*)\*/g, '$1<em>$2</em>')
  return substitute(styled, values, 'html')
}

function inlineText(raw: string, values: MergeValues): string {
  const withLinks = raw.replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (_all, label: string, url: string) => {
    const resolved = url.replace(MERGE, (_m, field: string) => (MERGE_FIELDS[field as MergeField]?.url ? (values[field as MergeField] ?? '') : ''))
    return safeHref(resolved) ? `${label} (${resolved})` : label
  })
  return substitute(withLinks.replace(/\*\*([^*]+)\*\*/g, '$1').replace(/(^|[^*])\*([^*\s][^*]*)\*/g, '$1$2'), values, 'text')
}

const resolveUrl = (url: string, values: MergeValues) => safeHref(url.replace(MERGE, (_m, field: string) => (MERGE_FIELDS[field as MergeField]?.url ? (values[field as MergeField] ?? '') : '')))

// BRE's look (ADR 0012) in email-safe form: tables, inline styles, web-safe
// fallbacks for Fraunces and Work Sans, no images (nothing loads from
// anywhere), always light.
const C = { page: '#f3f1ea', card: '#ffffff', ink: '#24211d', muted: '#5b554b', line: '#e2dccd', brand: '#6b0000', gold: '#b3893f', mist: '#f8f6ef' }
const SERIF = `Fraunces,Georgia,'Times New Roman',serif`
const SANS = `'Work Sans',-apple-system,'Segoe UI',Helvetica,Arial,sans-serif`

export function renderEmail(input: RenderInput): RenderedEmail {
  const { content, brand, merge } = input
  if (input.sendClass === 'marketing' && (!safeHref(input.links.preferenceUrl) || !safeHref(input.links.unsubscribeUrl))) {
    throw new RenderError('A marketing email needs its preference-center and unsubscribe links')
  }
  if (!brand.legalName.trim() || !brand.postalAddress.trim()) throw new RenderError('The brand needs its legal name and postal address')
  const values: MergeValues = { ...merge, preference_url: input.links.preferenceUrl ?? merge.preference_url }
  const subject = `${input.test ? '[TEST] ' : ''}${substitute(content.subject, values, 'text')}`.replace(/\s+/g, ' ').trim()
  const preheader = content.preheader ? substitute(content.preheader, values, 'text') : null
  const foot = lockedFooter(input)
  if (isImportedHtml(content)) return renderImportedEmail(input, content, values, subject, preheader, foot)

  const html: string[] = []
  const text: string[] = []
  for (const b of content.blocks) {
    if (b.type === 'heading') {
      html.push(`<h2 style="margin:28px 0 10px;font-family:${SERIF};font-size:24px;line-height:1.25;font-weight:600;color:${C.ink};letter-spacing:-0.01em">${inlineHtml(b.text, values, C.brand)}</h2>`)
      const t = inlineText(b.text, values)
      text.push(t, '-'.repeat(Math.min(t.length, 60)), '')
    } else if (b.type === 'text') {
      for (const para of b.text.split(/\n{2,}/)) {
        html.push(`<p style="margin:0 0 16px;font-family:${SANS};font-size:16px;line-height:1.6;color:${C.ink}">${inlineHtml(para, values, C.brand).replace(/\n/g, '<br>')}</p>`)
        text.push(inlineText(para, values), '')
      }
    } else if (b.type === 'button') {
      const href = resolveUrl(b.url, values)
      if (!href) continue
      html.push(
        `<table role="presentation" cellpadding="0" cellspacing="0" border="0" style="margin:8px 0 24px"><tr><td style="background:${C.brand};border-radius:4px"><a href="${escapeHtml(href)}" style="display:inline-block;padding:13px 26px;font-family:${SANS};font-size:14px;font-weight:600;letter-spacing:0.06em;text-transform:uppercase;color:#fdfaf3;text-decoration:none">${inlineHtml(b.label, values, '#fdfaf3')}</a></td></tr></table>`,
      )
      text.push(`${inlineText(b.label, values)}: ${href}`, '')
    } else if (b.type === 'list') {
      html.push(
        `<ul style="margin:0 0 18px;padding:0 0 0 20px;font-family:${SANS};font-size:16px;line-height:1.6;color:${C.ink}">${b.items.map((i) => `<li style="margin:0 0 6px">${inlineHtml(i, values, C.brand)}</li>`).join('')}</ul>`,
      )
      text.push(...b.items.map((i) => `  - ${inlineText(i, values)}`), '')
    } else if (b.type === 'event') {
      const href = b.url ? resolveUrl(b.url, values) : null
      html.push(
        `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="margin:8px 0 24px;border-left:3px solid ${C.gold};background:${C.mist}"><tr><td style="padding:16px 18px">` +
          `<p style="margin:0 0 4px;font-family:${SANS};font-size:12px;font-weight:600;letter-spacing:0.12em;text-transform:uppercase;color:#7f5c1d">${inlineHtml(b.when, values, C.brand)}</p>` +
          `<p style="margin:0;font-family:${SERIF};font-size:20px;line-height:1.3;font-weight:600;color:${C.ink}">${inlineHtml(b.name, values, C.brand)}</p>` +
          (b.where ? `<p style="margin:4px 0 0;font-family:${SANS};font-size:14px;color:${C.muted}">${inlineHtml(b.where, values, C.brand)}</p>` : '') +
          (href ? `<p style="margin:10px 0 0;font-family:${SANS};font-size:14px;font-weight:600"><a href="${escapeHtml(href)}" style="color:${C.brand}">Details and tickets &rarr;</a></p>` : '') +
          `</td></tr></table>`,
      )
      text.push(inlineText(b.when, values).toUpperCase(), inlineText(b.name, values), ...(b.where ? [inlineText(b.where, values)] : []), ...(href ? [href] : []), '')
    } else {
      html.push(`<hr style="border:0;border-top:1px solid ${C.line};margin:24px 0">`)
      text.push('* * *', '')
    }
  }

  const page = [
    '<!DOCTYPE html><html lang="en"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><meta name="color-scheme" content="light"><meta name="supported-color-schemes" content="light">',
    `<title>${escapeHtml(subject)}</title>`,
    // Phones: less padding. Clients that drop <style> keep the desktop
    // spacing, which still reads.
    `<style>@media (max-width:520px){.m-outer{padding:16px 8px!important}.m-card{padding:6px 20px 16px!important}.m-mast{font-size:18px!important}}</style></head>`,
    `<body style="margin:0;padding:0;background:${C.page}">`,
    preheader ? `<div style="display:none;max-height:0;overflow:hidden;opacity:0;color:transparent">${escapeHtml(preheader)}${'&#847;&zwnj;&nbsp;'.repeat(30)}</div>` : '',
    `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background:${C.page}"><tr><td class="m-outer" align="center" style="padding:28px 12px">`,
    `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="max-width:600px">`,
    `<tr><td class="m-mast" style="padding:0 4px 14px;font-family:${SERIF};font-size:20px;font-weight:600;color:${C.brand};letter-spacing:-0.01em">${escapeHtml(input.masthead?.trim() || brand.name)}</td></tr>`,
    `<tr><td class="m-card" style="background:${C.card};border-top:4px solid ${C.brand};border-radius:2px;padding:12px 36px 22px">`,
    input.test ? `<p style="margin:16px 0 0;padding:8px 12px;background:#fff4d6;border:1px solid ${C.gold};font-family:${SANS};font-size:13px;color:#7f5c1d"><strong>Test send.</strong> Only staff get this copy; the merge fields show sample values.</p>` : '',
    html.join(''),
    `</td></tr>`,
    `<tr><td style="padding:22px 4px 0;font-family:${SANS};font-size:12px;line-height:1.6;color:${C.muted}">${foot.html}</td></tr>`,
    `</table></td></tr></table></body></html>`,
  ].join('')

  const body = [...(input.test ? ['[TEST SEND: only staff get this copy]', ''] : []), ...text, ...foot.text].join('\n').replace(/\n{3,}/g, '\n\n')
  return { subject, preheader, html: page, text: body }
}

// The locked footer: who sent it and where they are (CAN-SPAM), why the
// person gets it, and (for marketing) the preference center and a
// one-click unsubscribe. A template can't remove or restyle it; an
// imported design gets exactly the same one.
function lockedFooter(input: RenderInput): { html: string; text: string[] } {
  const { brand } = input
  const pref = safeHref(input.links.preferenceUrl)
  const unsub = safeHref(input.links.unsubscribeUrl)
  const footLinks = [pref ? `<a href="${escapeHtml(pref)}" style="color:${C.muted};text-decoration:underline">Manage your email preferences</a>` : null, unsub ? `<a href="${escapeHtml(unsub)}" style="color:${C.muted};text-decoration:underline">Unsubscribe</a>` : null].filter(Boolean)
  const footer =
    `<p style="margin:0 0 8px">${escapeHtml(input.reason)}</p>` +
    (footLinks.length ? `<p style="margin:0 0 12px">${footLinks.join(' &nbsp;&middot;&nbsp; ')}</p>` : '') +
    `<p style="margin:0">${escapeHtml(brand.legalName)}<br>${escapeHtml(brand.postalAddress)}</p>`

  const footText = [
    '--',
    input.reason,
    ...(pref ? [`Manage your email preferences: ${pref}`] : []),
    ...(unsub ? [`Unsubscribe: ${unsub}`] : []),
    brand.legalName,
    brand.postalAddress,
  ]
  return { html: footer, text: footText }
}

// A #rgb or #rrggbb color light enough (relative luminance over 0.6) for
// the footer's muted text, else null.
function lightColor(hex: string | undefined): string | null {
  if (!hex) return null
  const h = hex.length === 4 ? hex.slice(1).split('').map((c) => c + c).join('') : hex.slice(1)
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16) / 255).map((c) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4))
  return 0.2126 * r! + 0.7152 * g! + 0.0722 * b! > 0.6 ? `#${h.toLowerCase()}` : null
}

const FOOTER_CELL = `padding:22px 4px 0;font-family:${SANS};font-size:12px;line-height:1.6;color:${C.muted}`
const TEST_BANNER = `<p style="margin:16px 0 0;padding:8px 12px;background:#fff4d6;border:1px solid ${C.gold};font-family:${SANS};font-size:13px;color:#7f5c1d"><strong>Test send.</strong> Only staff get this copy; the merge fields show sample values.</p>`

// An imported design (ADR 0042): the cleaned design as it was saved, its
// merge fields filled in (escaped), inside a wrapper its scoped styles
// can't reach out of, then Hub's locked footer.
function renderImportedEmail(input: RenderInput, content: ImportedHtmlContent, values: MergeValues, subject: string, preheader: string | null, foot: { html: string; text: string[] }): RenderedEmail {
  // A link field stands alone as a whole href (checked on save). With no
  // value (an operational email has no preference center) the link becomes
  // plain text, never a dead link.
  const linked = content.html.replace(/<a href="\{\{(mybre_url|preference_url)\}\}"/g, (_all, field: 'mybre_url' | 'preference_url') => {
    const href = safeHref(values[field])
    return href ? `<a href="${escapeHtml(href)}"` : '<a'
  })
  const legal = (raw: string, mode: 'html' | 'text') => raw.replace(/\{\{\s*brand_legal_name\s*\}\}/g, mode === 'html' ? escapeHtml(input.brand.legalName) : input.brand.legalName)
  const design = substitute(legal(linked, 'html'), values, 'html')
  // The footer sits on the design's own page color when it is light enough
  // for the footer's text (a Canva design usually sets one on <body>), so
  // the email reads as one piece; otherwise on Hub's.
  const band = lightColor(/background-color:\s*(#[0-9a-fA-F]{3}|#[0-9a-fA-F]{6})\b/.exec(content.wrapperStyle)?.[1]) ?? C.page
  const page = [
    '<!DOCTYPE html><html lang="en"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><meta name="color-scheme" content="light"><meta name="supported-color-schemes" content="light">',
    `<title>${escapeHtml(subject)}</title>`,
    `<style>.hub-design img{max-width:100%;height:auto}\n${content.styles}</style></head>`,
    `<body style="margin:0;padding:0;background:${band}">`,
    preheader ? `<div style="display:none;max-height:0;overflow:hidden;opacity:0;color:transparent">${escapeHtml(preheader)}${'&#847;&zwnj;&nbsp;'.repeat(30)}</div>` : '',
    input.test ? `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"><tr><td align="center" style="padding:0 12px 12px"><table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="max-width:600px"><tr><td>${TEST_BANNER}</td></tr></table></td></tr></table>` : '',
    `<div class="hub-design"${content.wrapperStyle ? ` style="${escapeHtml(content.wrapperStyle)}"` : ''}>${design}</div>`,
    `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background:${band}"><tr><td align="center" style="padding:0 12px 28px">`,
    `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="max-width:600px"><tr><td class="hub-footer" style="${FOOTER_CELL}">${foot.html}</td></tr></table>`,
    `</td></tr></table></body></html>`,
  ].join('')
  // A link field with no value leaves no empty "()" behind in the text.
  const text = substitute(legal(content.text, 'text').replace(/ ?\(\{\{(mybre_url|preference_url)\}\}\)/g, (all, field: 'mybre_url' | 'preference_url') => (safeHref(values[field]) ? all : '')), values, 'text')
  const body = [...(input.test ? ['[TEST SEND: only staff get this copy]', ''] : []), text, '', ...foot.text].join('\n').replace(/\n{3,}/g, '\n\n')
  return { subject, preheader, html: page, text: body }
}

// The double opt-in confirmation (plan §6.3, §13 item 8): operational mail
// with one job, a signed link that completes the grant. It quotes the
// wording the person agreed to and nothing they typed.
export function confirmationContent(input: { brandName: string; statementText: string; confirmUrl: string }): TemplateContent {
  return {
    subject: `Please confirm: ${input.brandName} email`,
    preheader: 'One click finishes your sign-up. If it wasn’t you, ignore this email.',
    blocks: [
      { type: 'heading', text: 'Confirm your subscription' },
      { type: 'text', text: `Someone, hopefully you, asked to get email from ${input.brandName} at this address. They agreed to this:` },
      { type: 'list', items: [input.statementText] },
      { type: 'button', label: 'Yes, subscribe me', url: input.confirmUrl },
      { type: 'text', text: 'If you didn’t ask for this, ignore this email: nothing more will come, and we won’t add you to any list.' },
    ],
  }
}

// ---------------------------------------------------- confirmation links

// /c/<token> on the forms host completes one waiting grant. The token names
// the waiting consent event and when the link stops working, signed under a
// key derived from HUB_CORE_ACTOR_SECRET; nothing is stored before use.
//   c1.<consentEventId>.<expiresAt unix seconds>.<signature>
const CONFIRM_TOKEN = /^c1\.(cev_[A-Za-z0-9]{8,40})\.(\d{9,11})\.([A-Za-z0-9_-]{32})$/
const enc = new TextEncoder()
async function hmac32(key: string, message: string): Promise<string> {
  const k = await crypto.subtle.importKey('raw', enc.encode(key), { name: 'HMAC', hash: 'SHA-256' }, false, ['sign'])
  let binary = ''
  for (const byte of new Uint8Array(await crypto.subtle.sign('HMAC', k, enc.encode(message)))) binary += String.fromCharCode(byte)
  return btoa(binary).replaceAll('+', '-').replaceAll('/', '_').replaceAll('=', '').slice(0, 32)
}

export async function signConfirmToken(secret: string, token: { eventId: string; expiresAt: number }): Promise<string> {
  if (!secret || secret.length < 32) throw new Error('The token secret must be at least 32 characters')
  const body = `c1.${token.eventId}.${token.expiresAt}`
  return `${body}.${await hmac32(`hub-comms-confirm-v1:${secret}`, body)}`
}

export async function verifyConfirmToken(secret: string, raw: string, nowMs = Date.now()): Promise<{ ok: true; eventId: string; expiresAt: number } | { ok: false; reason: 'malformed' | 'bad_signature' | 'expired' }> {
  const m = CONFIRM_TOKEN.exec(raw ?? '')
  if (!m) return { ok: false, reason: 'malformed' }
  const expected = await hmac32(`hub-comms-confirm-v1:${secret}`, raw.slice(0, raw.length - 33))
  let diff = expected.length === m[3]!.length ? 0 : 1
  for (let i = 0; i < expected.length && i < m[3]!.length; i += 1) diff |= expected.charCodeAt(i) ^ m[3]!.charCodeAt(i)
  if (diff !== 0) return { ok: false, reason: 'bad_signature' }
  if (Number(m[2]) * 1000 <= nowMs) return { ok: false, reason: 'expired' }
  return { ok: true, eventId: m[1]!, expiresAt: Number(m[2]) }
}

// -------------------------------------------------------------- segments

// A segment is a stored question, answered at send time (plan §7; BTownTix
// D-305): never a list of members. The shape is fixed, not a query
// language. Phase 2 answers it from Hub's own data only; the apps' audience
// reads (BTownTix buyers, myBRE members) don't exist yet.
export const SEGMENT_WORKER_STATUSES = ['active', 'applicant', 'inactive', 'alumni'] as const

export const SegmentCriterionSchema = z.discriminatedUnion('kind', [
  // Everyone whose address has an effective grant for the segment's brand,
  // purpose and channel: the subscriber list.
  z.object({ kind: z.literal('subscribers') }),
  z.object({
    kind: z.literal('workforce'),
    workerTypes: z.array(z.enum(WORKER_TYPES)).min(1).max(4),
    statuses: z.array(z.enum(SEGMENT_WORKER_STATUSES)).min(1).max(4),
    departmentIds: z.array(z.string().min(1).max(60)).max(20),
  }),
  z.object({ kind: z.literal('directors') }),
  z.object({ kind: z.literal('committee'), committeeId: z.string().min(1).max(60) }),
  z.object({ kind: z.literal('event_shifts'), eventId: z.string().min(1).max(60) }),
  // Phase 3: the people assigned to one shift (a roster).
  z.object({ kind: z.literal('shift'), shiftId: z.string().min(1).max(60) }),
  z.object({ kind: z.literal('form'), formId: z.string().min(1).max(60) }),
  z.object({ kind: z.literal('people'), personIds: z.array(z.string().min(1).max(60)).min(1).max(500) }),
])
export type SegmentCriterion = z.infer<typeof SegmentCriterionSchema>
export const SEGMENT_CRITERION_LABELS: Record<SegmentCriterion['kind'], string> = {
  subscribers: 'Everyone subscribed to this brand',
  workforce: 'Staff and volunteers',
  directors: 'Current directors',
  committee: 'A committee’s members',
  event_shifts: 'People on an event’s shifts',
  shift: 'People on one shift',
  form: 'People who submitted a form',
  people: 'A list of people',
}

export const SegmentDefinitionSchema = z.object({
  version: z.literal(1),
  // Anyone matching any include, minus anyone matching any exclude.
  include: z.array(SegmentCriterionSchema).min(1).max(10),
  exclude: z.array(SegmentCriterionSchema).max(10),
})
export type SegmentDefinition = z.infer<typeof SegmentDefinitionSchema>

// Why an address isn't in an audience, in gate order (plan §6.4 rule 1).
export const EXCLUSION_REASONS = ['person_inactive', 'no_address', 'no_push_device', 'suppressed', 'no_consent', 'pending_confirmation', 'withdrawn', 'unverified', 'frequency_cap', 'excluded'] as const
export type ExclusionReason = (typeof EXCLUSION_REASONS)[number]
export const EXCLUSION_REASON_LABELS: Record<ExclusionReason, string> = {
  person_inactive: 'Person no longer active or merged away',
  no_address: 'No address for this channel',
  no_push_device: 'No device with myBRE push turned on',
  suppressed: 'Suppressed (bounced, complained or asked to stop)',
  no_consent: 'No consent on record',
  pending_confirmation: 'Waiting for confirmation (double opt-in)',
  withdrawn: 'Unsubscribed',
  unverified: 'Address not verified',
  frequency_cap: 'Frequency cap reached',
  excluded: 'Matched an exclusion',
}

export type AudiencePreview = {
  asOf: string
  // Every source Phase 2 reads is Hub's own, so a preview is never partial;
  // the field is here for the app reads that come later (plan §7).
  partial: boolean
  candidates: number
  included: number
  excluded: Partial<Record<ExclusionReason, number>>
  // A page of who's in: names only with people.read, addresses masked otherwise.
  sample: { personId: string | null; name: string | null; address: string }[]
  truncated: boolean
}

// --------------------------------------------------------------- campaigns

export const CAMPAIGN_STATUSES = ['draft', 'awaiting_approval', 'approved', 'scheduled', 'sending', 'sent', 'canceled'] as const
export type CampaignStatus = (typeof CAMPAIGN_STATUSES)[number]
export const CAMPAIGN_STATUS_LABELS: Record<CampaignStatus, string> = {
  draft: 'Draft',
  awaiting_approval: 'Awaiting approval',
  approved: 'Approved',
  scheduled: 'Scheduled',
  sending: 'Sending',
  sent: 'Sent',
  canceled: 'Canceled',
}

// ---------------------------------------------------------------- settings

// The sending settings (comms_send_settings). Every default is provisional
// until the owner decides (plan §13; ADR 0035).
export type CommsSendSettings = {
  // The campaign provider the owner chose, or none yet. With none, or with
  // no credential, campaigns run simulated. Credentials are Worker secrets,
  // never settings.
  campaignProvider: 'postmark' | 'none'
  postmarkStream: string
  // Every marketing campaign needs an approval (plan §13 item 12).
  approvalForMarketing: boolean
  // Other campaigns need one over this many recipients.
  approvalOperationalOver: number
  // At most this many marketing emails per brand per address per window.
  frequencyCapCount: number
  frequencyCapHours: number
  // Marketing email only to an address verified by BRE Auth, by double
  // opt-in, by its own signed link, or by an earlier delivered message.
  requireVerifiedForMarketing: boolean
  ratePerMinuteCampaign: number
  ratePerMinuteOperational: number
  maxAudience: number
  // Gmail's guidance (plan §8): bounces under 2%, complaints under 0.1%.
  bounceRateLimitBp: number
  complaintRateLimitBp: number
  softBounceLimit: number
  softBounceDays: number
  confirmLinkDays: number
  mybreUrl: string
  // When automatic confirmation emails started (HUB_COMMS_CONFIRM first ran
  // on): waiting grants older than this get one only when staff ask.
  confirmationsSince: string | null
  updatedAt: string | null
  updatedByName: string | null
}

export const SendSettingsSchema = z.object({
  campaignProvider: z.enum(['postmark', 'none']),
  postmarkStream: z.string().trim().regex(/^[a-z0-9-]{1,60}$/),
  approvalForMarketing: z.boolean(),
  approvalOperationalOver: z.number().int().min(0).max(100_000),
  frequencyCapCount: z.number().int().min(1).max(50),
  frequencyCapHours: z.number().int().min(1).max(24 * 31),
  requireVerifiedForMarketing: z.boolean(),
  ratePerMinuteCampaign: z.number().int().min(1).max(5_000),
  ratePerMinuteOperational: z.number().int().min(1).max(1_000),
  maxAudience: z.number().int().min(1).max(50_000),
  bounceRateLimitBp: z.number().int().min(1).max(10_000),
  complaintRateLimitBp: z.number().int().min(1).max(10_000),
  softBounceLimit: z.number().int().min(1).max(20),
  softBounceDays: z.number().int().min(1).max(365),
  confirmLinkDays: z.number().int().min(1).max(90),
  mybreUrl: z.string().trim().regex(/^https:\/\/[^\s<>"']+$/),
})

// ------------------------------------------------------- the dispatcher

// The scheduled dispatcher's actor: reaches the dispatch routes only.
export const COMMS_DISPATCH_SUB = 'system:comms-dispatch'
// Claims left unreported this long become outcome_unknown, never resent.
export const MESSAGE_LEASE_MS = 15 * 60 * 1000
export const MESSAGE_MAX_ATTEMPTS = 3
// Soft failures wait 5, then 20 minutes.
export const retryDelayMs = (attempts: number) => 5 * 60 * 1000 * 4 ** Math.max(0, attempts - 1)

// One rendered message, claimed, for the web Worker to send.
export type OutboxEnvelope = {
  messageId: string
  claimId: string
  sendClass: SendClass
  to: string
  from: { email: string; name: string }
  replyTo: string | null
  subject: string
  html: string
  text: string
  // RFC 8058 headers for marketing (List-Unsubscribe and -Post).
  headers: Record<string, string>
  // For the provider's metadata, so a webhook finds its row.
  brandKey: string
  campaignId: string | null
  test: boolean
}

export type DispatchClaim = {
  claimId: string
  envelopes: OutboxEnvelope[]
  abandoned: number
  skipped: number
  snapshots: number
  // The provider settings this claim was made under (credentials stay in
  // the web Worker's secrets).
  campaignProvider: 'postmark' | 'none'
  postmarkStream: string
  // Phase 3: texts claimed in the same run, and the texting provider.
  smsEnvelopes?: SmsEnvelope[]
  smsProvider?: 'twilio' | 'none'
  // Phase 4: pushes claimed in the same run, and the push provider.
  pushEnvelopes?: PushEnvelope[]
  pushProvider?: 'mybre' | 'none'
}

// What the web Worker did with one message.
//   accepted   the provider took it (its message id)
//   simulated  simulated mode; nothing was sent
//   retry      a soft failure (rate limit, quota, provider busy): queued again
//              with backoff, or failed after the last attempt
//   failed     refused for good (bad address, credentials, route)
//   suppressed the provider says the address is inactive (a provider
//              suppression is recorded too)
//   unknown    the request may have reached the provider and no answer came
//              back: outcome_unknown, never resent
//   released   the dispatcher is switched off: back to queued, no attempt used
export type DispatchOutcome = {
  messageId: string
  outcome: 'accepted' | 'simulated' | 'retry' | 'failed' | 'suppressed' | 'unknown' | 'released'
  provider: CommsProvider
  providerMessageId?: string | null
  detail?: string | null
  // A text Twilio refused because the number replied STOP to that sender
  // (21610), or isn't a mobile number: Hub records it on its own list.
  smsSuppression?: 'sms_stop' | 'invalid_number' | null
}

// A provider event, normalized (parseEvent in plan §5.5). Simulated events
// come through the same shape and the same code path.
export type ProviderEvent = {
  provider: CommsProvider
  providerEventId: string
  hubMessageId: string | null
  providerMessageId: string | null
  kind: 'delivered' | 'deferred' | 'bounced' | 'complained' | 'unsubscribed' | 'resubscribed' | 'sent' | 'undelivered'
  hard: boolean
  // Texts: the provider's error code (Twilio's ErrorCode), when it gave one.
  errorCode?: string | null
  address: string | null
  at: string
  detail: string | null
  payloadHash: string | null
}

export type ProviderStatus = {
  campaign: { provider: 'postmark' | 'none'; mode: 'live' | 'simulated' | 'not_set_up'; reason: string }
  operational: { provider: 'cloudflare'; mode: 'live' | 'simulated' | 'not_set_up'; reason: string }
  sendSwitch: boolean
  confirmSwitch: boolean
}

// ----------------------------------------------------------- request bodies

export const NewTemplateSchema = z.object({
  name: z.string().trim().min(1).max(120),
  brandKey: z.string().regex(COMMS_KEY),
  purposeKey: z.string().regex(COMMS_KEY),
  channel: z.enum(['email', 'sms', 'push']).default('email'),
  // An email template's blocks, a text template's text, or a push's title, text and link.
  // An imported design (ADR 0042) names its import; it comes first, since a
  // text template's { text } would also match it.
  content: z.union([HtmlTemplateRefSchema, TemplateContentSchema, SmsTemplateContentSchema, PushTemplateContentSchema]),
})
export const SegmentBodySchema = z.object({
  name: z.string().trim().min(1).max(120),
  description: z.string().trim().max(500).nullable(),
  brandKey: z.string().regex(COMMS_KEY),
  purposeKey: z.string().regex(COMMS_KEY),
  definition: SegmentDefinitionSchema,
})
export const CampaignBodySchema = z.object({
  name: z.string().trim().min(1).max(160),
  brandKey: z.string().regex(COMMS_KEY),
  purposeKey: z.string().regex(COMMS_KEY),
  templateVersionId: z.string().min(1).max(60).nullable(),
  segmentId: z.string().min(1).max(60).nullable(),
  eventName: z.string().trim().max(200).nullable(),
  eventDate: z.string().trim().max(100).nullable(),
  channel: z.enum(['email', 'sms', 'push']).default('email'),
})
